import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useContentReviewerNudgeOnCriteria } from '../content-reviewer/useContentReviewerNudgeOnCriteria.tsx';
import { useContentReviewerNudgeOnCriteriaBaseEligibility } from '../content-reviewer/useContentReviewerNudgeOnCriteriaBaseEligibility.ts';
import { useStatusUpdatesAutomationNudgeAction } from '../status-updates/useStatusUpdatesAutomationNudgeAction.tsx';

import { useConfluenceAutomationsBaseEligibility } from './useConfluenceAutomationsBaseEligibility.ts';
import { useStudioPermissions } from './useStudioPermissions.ts';

import type {
	useConfluenceAutomationNudgeActionsQuery$data,
	useConfluenceAutomationNudgeActionsQuery$variables,
} from './__generated__/useConfluenceAutomationNudgeActionsQuery.graphql.ts';

export interface AutomationNudgeActionEntry {
	actionId: string;
	action: ConversationAction;
}

export const useConfluenceAutomationNudgeActions = (
	contentId: string,
): AutomationNudgeActionEntry[] => {
	const hasBaseEligibility = useConfluenceAutomationsBaseEligibility();
	const pageMode = useGetPageMode();

	const hasUserSeenStatusUpdatesAutomationNudge = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_STATUS_UPDATES,
	);
	const includeStatusUpdatesAutomation =
		!hasUserSeenStatusUpdatesAutomationNudge && fg('cc_automations_discovery_nudges');

	const includeContentReviewerNudge = useContentReviewerNudgeOnCriteriaBaseEligibility();
	const isAnyNudgeEligible = includeStatusUpdatesAutomation || includeContentReviewerNudge;

	const data = useQueryInterop<
		useConfluenceAutomationNudgeActionsQuery$data,
		useConfluenceAutomationNudgeActionsQuery$variables
	>(
		graphql`
			query useConfluenceAutomationNudgeActionsQuery(
				$contentId: ID!
				$includeStatusUpdatesAutomation: Boolean!
				$includeContentReviewerNudge: Boolean!
				$isDraft: Boolean!
			) {
				user: confluence_atlassianUser(current: true) {
					__typename
					id
					...useStatusUpdatesAutomationNudgeAction_user
						@alias(as: "statusUpdatesAutomationUser")
						@include(if: $includeStatusUpdatesAutomation)
				}
				singleContent(id: $contentId) {
					__typename
					id
					...useContentReviewerNudgeOnCriteria_content
						@alias(as: "contentReviewerContent")
						@arguments(isDraft: $isDraft)
						@include(if: $includeContentReviewerNudge)
					...useStatusUpdatesAutomationNudgeAction_content
						@alias(as: "statusUpdatesAutomationContent")
						@include(if: $includeStatusUpdatesAutomation)
				}
			}
		`,
		{
			variables: {
				contentId,
				includeStatusUpdatesAutomation,
				includeContentReviewerNudge,
				isDraft: pageMode === PageMode.EDIT,
			},
			skip: !hasBaseEligibility || !contentId || !isAnyNudgeEligible,
		},
	);

	const contentReviewerNudgeAction = useContentReviewerNudgeOnCriteria({
		contentFragmentRef: data.data?.singleContent?.contentReviewerContent,
	});

	const statusUpdatesAutomationNudgeAction = useStatusUpdatesAutomationNudgeAction({
		contentFragmentRef: data.data?.singleContent?.statusUpdatesAutomationContent,
		userFragmentRef: data.data?.user?.statusUpdatesAutomationUser,
	});

	const matchedActions = useMemo<AutomationNudgeActionEntry[]>(
		() =>
			[
				{ actionId: 'status-updates-automation', action: statusUpdatesAutomationNudgeAction },
				{ actionId: 'content-reviewer-nudge', action: contentReviewerNudgeAction },
			].filter((entry): entry is AutomationNudgeActionEntry => entry.action != null),
		[statusUpdatesAutomationNudgeAction, contentReviewerNudgeAction],
	);

	// Defer the Studio permission check until at least one nudge has matched, so
	// the permission query never runs for the common case where nothing matches —
	// while still only running it once for all nudges.
	const { canCreateStudioAutomation } = useStudioPermissions({ skip: matchedActions.length === 0 });

	return useMemo<AutomationNudgeActionEntry[]>(
		() => (canCreateStudioAutomation ? matchedActions : []),
		[matchedActions, canCreateStudioAutomation],
	);
};
